import { Settings } from '@lightningjs/sdk'
import NpawPlugin from 'npaw-plugin-es5'
import hlsAdapter from '../../static/config/youbora-adapter-7.0.5-hlsjs-js.json'
import { AppConfig } from '../services/appConfig'
import { PlayerModel } from '../types/flo-experience'
import { getUserData } from './authentication'
import { getAppName, getAppVersion } from './info'
import { isStagingEnabled } from './settingsUtils'

type PlayerYouboraOptions = {
  data: PlayerModel
  isLive: boolean
  resourceUrl: string
  isUpNext: boolean
  title?: string
}

const YOUBORA_VIDEO_KEY = 'flosports' // Just to identify the adapter for removal

export const createYouboraPlugin = () => {
  const youboraAccountCode = Settings.get(
    'app',
    isStagingEnabled()
      ? 'env.APP_YOUBORA_ACCOUNT_CODE_STAGING'
      : 'env.APP_YOUBORA_ACCOUNT_CODE_PROD',
  )

  return new NpawPlugin(youboraAccountCode, <any>{ balancer: { enabled: false } })
}

export const registerYouboraPlugin = (hls, youboraOptions, onRegisterCallback) => {
  ;(<any>AppConfig.shared.npawPlugin).cdnBalancer.isEnabled = false
  AppConfig.shared.npawPlugin.setLogLevel(2)
  AppConfig.shared.npawPlugin.registerAdapterFromString(
    hls,
    JSON.stringify(hlsAdapter),
    youboraOptions,
    YOUBORA_VIDEO_KEY,
    onRegisterCallback,
  )
}

export const removeYouboraPlugin = () => {
  AppConfig.shared.npawPlugin.removeAdapter(YOUBORA_VIDEO_KEY)
}

export const setYouboraVideoOptions = async (options: PlayerYouboraOptions) => {
  AppConfig.shared.npawPlugin.setVideoOptions(
    await getYouboraVideoOptions(options),
    YOUBORA_VIDEO_KEY,
  )
  AppConfig.shared.npawPlugin.getAdapter(YOUBORA_VIDEO_KEY).fireStart()
}

export const youboraFireStart = () => {
  AppConfig.shared.npawPlugin.getAdapter(YOUBORA_VIDEO_KEY).fireStart()
}

export const getYouboraVideoOptions = async ({
  data,
  isLive,
  resourceUrl,
  isUpNext,
  title,
}: PlayerYouboraOptions) => {
  const videoOptions = {
    'app.name': getAppName(),
    'app.releaseVersion': getAppVersion(),
    'user.name': getUserData()?.user_id ?? null,
    'content.title': title ?? data?.title ?? data?.analytics?.video_analytics?.title,
    'content.isLive': isLive,
    'content.resource': resourceUrl,
    'content.customDimension.1': data?.analytics?.site_name, // sports vertical: FloWrestling, etc.
    'content.customDimension.2': data?.analytics?.extra_context?.live_id, // event ID
    'content.customDimension.3': data?.analytics?.video_analytics?.event_name, // short event name
    'content.customDimension.4': data?.analytics?.stream_id, // stream ID
    'content.customDimension.5': data?.analytics?.video_analytics?.cast_client, // cast client
    'content.customDimension.6': data?.analytics?.video_analytics?.viewing_mode ?? 'Preview', // view mode (Preview / Full)
    'content.customDimension.7': data?.analytics?.collection_slug, // collection slug
    'content.customDimension.9': isUpNext ? 1 : 0, // start from autoplay = 1, starts from user = 0
    'content.customDimension.10': data?.node_id,
    'content.customDimension.11': data?.analytics?.is_premium ? 1 : 0, // is premium (1) or is free(0)
    'content.customDimension.12': AppConfig.shared.analyticsService.instance?.user().anonymousId(), // anonymous id
    'content.customDimension.13': '', // TODO: tournament
    'content.customDimension.14': '', // TODO: league
    'content.customDimension.15': (await AppConfig.shared.appSettings.device.getModel()).name,
    'content.customDimension.16': AppConfig.shared.appSettings.device.getVendor(),
  }

  return videoOptions
}
